import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { formatNYDate } from '../utils/datetime';
import './CaseSearch.css';

const CaseSearch = () => {
  const { getAllCases } = useAppContext();
  const [allCases, setAllCases] = useState([]);
  const [query, setQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const filterParam = searchParams.get('filter');
  const defaultStatus = searchParams.get('status') || '';
  const pageParam = parseInt(searchParams.get('page'), 10);
  const pageSizeParam = parseInt(searchParams.get('pageSize'), 10);

  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [countyFilter, setCountyFilter] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(isNaN(pageParam) || pageParam < 1 ? 1 : pageParam);
  const [pageSize, setPageSize] = useState(isNaN(pageSizeParam) || pageSizeParam < 1 ? 25 : pageSizeParam);
  const [loading, setLoading] = useState(true);
  const [uniqueCounties, setUniqueCounties] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await getAllCases();
      setAllCases(data);

      const counties = Array.from(
        new Set(data.map((c) => c.incidentCounty).filter(Boolean))
      ).sort();
      setUniqueCounties(counties);
      setLoading(false);
    };
    load();
  }, [getAllCases]);

  useEffect(() => {
    handleSearch();
  }, [query, statusFilter, countyFilter, sortOption, allCases]);

  useEffect(() => {
    // Update URL with current page and page size
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page);
      newParams.set('pageSize', pageSize);
      return newParams;
    });
  }, [page, pageSize, setSearchParams]);

  const formatDate = (dateStr) =>
    formatNYDate(dateStr, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

  const handleSearch = () => {
    const q = query.toLowerCase();
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const weekLater = new Date();
    weekLater.setDate(todayStart.getDate() + 7);

    let results = allCases.filter((caseObj) => {
      const values = Object.values(caseObj)
        .filter((v) => typeof v === 'string' || typeof v === 'number')
        .join(' ')
        .toLowerCase();
      return values.includes(q);
    });

    if (statusFilter) {
      results = results.filter(
        (c) => (c.status || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (countyFilter) {
      results = results.filter(
        (c) => (c.incidentCounty || '').toLowerCase() === countyFilter.toLowerCase()
      );
    }

    if (filterParam === 'thisWeek') {
      results = results
        .filter((c) => {
          const dt = c.courtDateTime ? new Date(c.courtDateTime) : null;
          return dt && dt >= todayStart && dt <= weekLater;
        })
        .sort((a, b) => new Date(a.courtDateTime) - new Date(b.courtDateTime));
    }

    if (sortOption === 'az') {
      results.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
    } else if (sortOption === 'za') {
      results.sort((a, b) => (b.lastName || '').localeCompare(a.lastName || ''));
    } else if (sortOption === 'soonest') {
      const withDate = results.filter(c => !!c.courtDateTime);
      const withoutDate = results.filter(c => !c.courtDateTime);
      withDate.sort((a, b) => new Date(a.courtDateTime) - new Date(b.courtDateTime));
      results = [...withDate, ...withoutDate];
    } else if (sortOption === 'latest') {
      const withDate = results.filter(c => !!c.courtDateTime);
      const withoutDate = results.filter(c => !c.courtDateTime);
      withDate.sort((a, b) => new Date(b.courtDateTime) - new Date(a.courtDateTime));
      results = [...withDate, ...withoutDate];
    } else if (sortOption === 'upcoming') {
      const withUpcoming = results.filter((c) => {
        const dt = c.courtDateTime ? new Date(c.courtDateTime) : null;
        return dt && dt >= todayStart;
      });
      const withoutDate = results.filter((c) => !c.courtDateTime || new Date(c.courtDateTime) < todayStart);
      withUpcoming.sort((a, b) => new Date(a.courtDateTime) - new Date(b.courtDateTime));
      results = [...withUpcoming, ...withoutDate];
    }

    setFiltered(results);
  };

  const startIndex = (page - 1) * pageSize;
  const currentPageData = filtered.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  const getBadgeClass = (prefix, value) =>
    `status-badge ${prefix}-${(value || '').toLowerCase().replace(/\s/g, '-')}`;

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  return (
    <div className="container search-page">
      <div className="search-header">
        <h2>Search Cases</h2>
        <p className="search-subtitle">Find and manage your cases with advanced filtering</p>
      </div>

      {loading ? (
        <div className="loading-state">
          <p>Loading cases...</p>
        </div>
      ) : (
        <>
          <div className="search-section">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search by name, ticket number, case type..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="search-input"
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filters-row">
              <div className="filter-group">
                <label>Disposition</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All Dispositions</option>
                  <option value="NEW">NEW</option>
                  <option value="Open">Open</option>
                  <option value="Waiting on Date">Waiting on Date</option>
                  <option value="MISSED">MISSED</option>
                  <option value="GUILTY">GUILTY</option>
                  <option value="NOT GUILTY">NOT GUILTY</option>
                  <option value="REDUCED">REDUCED</option>
                  <option value="UNHIRED">UNHIRED</option>
                  <option value="PLED">PLED</option>
                  <option value="Closed">Closed</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="filter-group">
                <label>County</label>
                <select value={countyFilter} onChange={(e) => setCountyFilter(e.target.value)}>
                  <option value="">All Counties</option>
                  {uniqueCounties.map((county) => (
                    <option key={county} value={county}>{county}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Sort By</label>
                <select value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
                  <option value="">Default Order</option>
                  <option value="az">Name A-Z</option>
                  <option value="za">Name Z-A</option>
                  <option value="soonest">Soonest Court Date</option>
                  <option value="latest">Latest Court Date</option>
                  <option value="upcoming">Upcoming Only</option>
                </select>
              </div>
            </div>
          </div>

          <div className="results-summary">
            <div className="results-info">
              <p>
                Showing <strong>{startIndex + 1}-{Math.min(startIndex + pageSize, filtered.length)}</strong> of <strong>{filtered.length}</strong> results
              </p>
            </div>
            <div className="page-size-selector">
              <label htmlFor="pageSize">Show:</label>
              <select 
                id="pageSize"
                value={pageSize} 
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
              <span>per page</span>
            </div>
          </div>

          <table className="case-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>County</th>
                <th>Ticket#</th>
                <th>Case Type</th>
                <th>Disposition</th>
                <th>Court Date</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((c, i) => (
                <tr 
                  key={c.id || i}
                  className="clickable-row"
                  onClick={() => navigate(`/case/${c.id}`)}
                >
                  <td>{`${c.firstName} ${c.lastName}`}</td>
                  <td>{c.incidentCounty || '-'}</td>
                  <td>{c.appearanceNumber}</td>
                  <td>{c.caseType}</td>
                  <td>
                    <span className={getBadgeClass('status', c.status)}>{c.status}</span>
                  </td>
                  <td>{c.courtDateTime ? formatDate(c.courtDateTime) : '-'}</td>
                  <td>
                    <Link 
                      to={`/case/${c.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button className="view-button">View</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length > pageSize && (
            <div className="pagination">
              <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>⬅ Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page === totalPages}>Next ➡</button>
            </div>
          )}
        </>
      )}

      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default CaseSearch;
